import {
  Button,
  DialogActions,
  DialogContent,
  Dialog,
  DialogTitle,
  Fab,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import './ExportQuote.css'
import { useEffect, useState } from 'react'
import { useQuoteLines } from '../../lines/QuoteLinesContext'
import { salesforceAccountType, salesforceContactType } from 'src/types'
import axios from 'axios'
import { QuotePdfPreview, downloadQuotePdf } from './QuotePdfDocument'
import { useRepCodes } from 'src/hooks'
import {
  AddressDetails,
  ContactDetails,
  EMPTY_ADDRESS,
  EMPTY_CONTACT,
  aeFromRepCode,
  isrFromRepCode,
  shipToFromCrm,
  soldToFromCrm,
} from './quotePdfModel'

type DialogKind = 'shipping' | 'isr' | 'ae' | 'soldTo' | 'comments'

function addressMissing(details: AddressDetails): Record<keyof AddressDetails, boolean> {
  return {
    accountCompany: !details.accountCompany.trim(),
    contactAttn: !details.contactAttn.trim(),
    addressLine1: !details.addressLine1.trim(),
    city: !details.city.trim(),
    state: !details.state.trim(),
    zipCode: !details.zipCode.trim(),
    phone: !details.phone.trim(),
    email: !details.email.trim(),
  }
}

function contactMissing(details: ContactDetails): Record<keyof ContactDetails, boolean> {
  return {
    name: !details.name.trim(),
    title: !details.title.trim(),
    phone: !details.phone.trim(),
    email: !details.email.trim(),
  }
}

export default function ExportQuote() {
  const { quote, lineItems, quoteId, setQuote } = useQuoteLines()
  const [repCodes] = useRepCodes()
  const [openDialog, setOpenDialog] = useState<DialogKind | null>(null)
  const [showErrors, setShowErrors] = useState(false)

  const [shippingDetails, setShippingDetails] = useState<AddressDetails>({ ...EMPTY_ADDRESS })
  const [soldToDetails, setSoldToDetails] = useState<AddressDetails>({ ...EMPTY_ADDRESS })
  const [isrDetails, setIsrDetails] = useState<ContactDetails>(
    isrFromRepCode(repCodes.find(r => r.RepCode === quote.RepCode) ?? null),
  )
  const [aeDetails, setAeDetails] = useState<ContactDetails>(
    aeFromRepCode(repCodes.find(r => r.RepCode === quote.RepCode) ?? null),
  )

  const [draftAddress, setDraftAddress] = useState<AddressDetails>({ ...EMPTY_ADDRESS })
  const [draftContact, setDraftContact] = useState<ContactDetails>({ ...EMPTY_CONTACT })
  const [draftComments, setDraftComments] = useState('')

  useEffect(() => {
    if (!quoteId) return
    axios
      .get(`/quote/${quoteId}`)
      .then(res => {
        const crmAccount = (res.data.crmAccount ?? null) as salesforceAccountType | null
        const crmContact = (res.data.crmContact ?? null) as salesforceContactType | null
        setSoldToDetails(soldToFromCrm(crmAccount, crmContact))
        setShippingDetails(shipToFromCrm(crmAccount, crmContact))
      })
      .catch(err => console.error(err))
  }, [quoteId])

  const closeDialog = () => {
    setOpenDialog(null)
    setShowErrors(false)
  }

  const openShippingDialog = () => {
    setDraftAddress({ ...shippingDetails })
    setShowErrors(false)
    setOpenDialog('shipping')
  }

  const openSoldToDialog = () => {
    setDraftAddress({ ...soldToDetails })
    setShowErrors(false)
    setOpenDialog('soldTo')
  }

  const openIsrDialog = () => {
    setDraftContact({ ...isrDetails })
    setShowErrors(false)
    setOpenDialog('isr')
  }

  const openAeDialog = () => {
    setDraftContact({ ...aeDetails })
    setShowErrors(false)
    setOpenDialog('ae')
  }

  const openCommentsDialog = () => {
    setDraftComments(quote.Comments ?? '')
    setShowErrors(false)
    setOpenDialog('comments')
  }

  const saveShippingDetails = () => {
    if (Object.values(addressMissing(draftAddress)).some(Boolean)) {
      setShowErrors(true)
      return
    }
    setShippingDetails({ ...draftAddress })
    closeDialog()
  }

  const saveSoldToDetails = () => {
    if (Object.values(addressMissing(draftAddress)).some(Boolean)) {
      setShowErrors(true)
      return
    }
    setSoldToDetails({ ...draftAddress })
    closeDialog()
  }

  const saveIsrDetails = () => {
    if (Object.values(contactMissing(draftContact)).some(Boolean)) {
      setShowErrors(true)
      return
    }
    setIsrDetails({ ...draftContact })
    closeDialog()
  }

  const saveAeDetails = () => {
    if (Object.values(contactMissing(draftContact)).some(Boolean)) {
      setShowErrors(true)
      return
    }
    setAeDetails({ ...draftContact })
    closeDialog()
  }

  const saveComments = () => {
    const comments = draftComments.trim() || null
    setQuote({ ...quote, Comments: comments })
    if (quoteId) {
      axios
        .put(`/quote/${quoteId}`, { quote: { Comments: comments, GovPayTerms: quote.GovPayTerms } })
        .catch(err => console.error(err))
    }
    closeDialog()
  }

  return (
    <div className="export-quote-wrapper">
      <div className="export-quote">
        <div className="export-quote-header">
          <div className="export-quote-header-left">
            <Typography variant="h6">Edit</Typography>
            <Button variant="outlined" size="small" onClick={openSoldToDialog}>
              Sold To Details
            </Button>
            <Button variant="outlined" size="small" onClick={openShippingDialog}>
              Shipping Details
            </Button>
            <Button variant="outlined" size="small" onClick={openIsrDialog}>
              ISR Details
            </Button>
            <Button variant="outlined" size="small" onClick={openAeDialog}>
              AE Details
            </Button>
          </div>
          <div className="export-quote-header-right">
            <Button variant="contained" color="primary" size="small" onClick={openCommentsDialog}>
              Add Comment
            </Button>
          </div>
        </div>
        <QuotePdfPreview
          className="export-quote-preview"
          quote={quote}
          lineItems={lineItems}
          soldToDetails={soldToDetails}
          shippingDetails={shippingDetails}
          isrDetails={isrDetails}
          aeDetails={aeDetails}
        />
      </div>
      <Fab
        className="export-quote-fab"
        variant="extended"
        size="large"
        color="primary"
        onClick={() =>
          downloadQuotePdf({ quote, lineItems, soldToDetails, shippingDetails, isrDetails, aeDetails }).catch(err =>
            console.error(err),
          )
        }
      >
        Export
      </Fab>

      <Dialog
        className="export-quote-dialog"
        open={openDialog === 'soldTo'}
        onClose={closeDialog}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Sold To Details</DialogTitle>
        <DialogContent>
          <AddressFields details={draftAddress} onChange={setDraftAddress} showErrors={showErrors} />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeDialog}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveSoldToDetails}>
            Save Details
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        className="export-quote-dialog"
        open={openDialog === 'shipping'}
        onClose={closeDialog}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Shipping Details</DialogTitle>
        <DialogContent>
          <AddressFields details={draftAddress} onChange={setDraftAddress} showErrors={showErrors} />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeDialog}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveShippingDetails}>
            Save Details
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog className="export-quote-dialog" open={openDialog === 'isr'} onClose={closeDialog} fullWidth maxWidth="lg">
        <DialogTitle>ISR Details</DialogTitle>
        <DialogContent>
          <ContactFields details={draftContact} onChange={setDraftContact} showErrors={showErrors} />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeDialog}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveIsrDetails}>
            Save Details
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog className="export-quote-dialog" open={openDialog === 'ae'} onClose={closeDialog} fullWidth maxWidth="lg">
        <DialogTitle>AE Details</DialogTitle>
        <DialogContent>
          <ContactFields details={draftContact} onChange={setDraftContact} showErrors={showErrors} />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeDialog}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveAeDetails}>
            Save Details
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        className="export-quote-dialog"
        open={openDialog === 'comments'}
        onClose={closeDialog}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Add Comment</DialogTitle>
        <DialogContent>
          <div className="export-quote-comments-field">
            <TextField
              fullWidth
              multiline
              minRows={4}
              size="small"
              label="Comments"
              value={draftComments}
              onChange={e => setDraftComments(e.target.value)}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeDialog}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveComments}>
            Save Comment
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

function AddressFields({
  details,
  onChange,
  showErrors,
}: {
  details: AddressDetails
  onChange: (next: AddressDetails) => void
  showErrors: boolean
}) {
  const missing = addressMissing(details)
  const err = (key: keyof AddressDetails, label: string) => (showErrors && missing[key] ? `${label} is required` : '')

  return (
    <Grid container spacing="16px" width="100%">
      <Grid size={12}>
        <Typography>Fields marked with a * are required.</Typography>
      </Grid>
      <Grid size={6}>
        <TextField
          fullWidth
          required
          size="small"
          label="Account/Company"
          value={details.accountCompany}
          error={showErrors && missing.accountCompany}
          helperText={err('accountCompany', 'Account/Company')}
          onChange={e => onChange({ ...details, accountCompany: e.target.value })}
        />
      </Grid>
      <Grid size={6}>
        <TextField
          fullWidth
          required
          size="small"
          label="Contact (Attn:)"
          value={details.contactAttn}
          error={showErrors && missing.contactAttn}
          helperText={err('contactAttn', 'Contact')}
          onChange={e => onChange({ ...details, contactAttn: e.target.value })}
        />
      </Grid>
      <Grid size={6}>
        <TextField
          fullWidth
          required
          size="small"
          label="Address Line 1"
          value={details.addressLine1}
          error={showErrors && missing.addressLine1}
          helperText={err('addressLine1', 'Address')}
          onChange={e => onChange({ ...details, addressLine1: e.target.value })}
        />
      </Grid>
      <Grid size={6}>
        <TextField
          fullWidth
          required
          size="small"
          label="City"
          value={details.city}
          error={showErrors && missing.city}
          helperText={err('city', 'City')}
          onChange={e => onChange({ ...details, city: e.target.value })}
        />
      </Grid>
      <Grid size={6}>
        <TextField
          fullWidth
          required
          size="small"
          label="State"
          value={details.state}
          error={showErrors && missing.state}
          helperText={err('state', 'State')}
          onChange={e => onChange({ ...details, state: e.target.value })}
        />
      </Grid>
      <Grid size={6}>
        <TextField
          fullWidth
          required
          size="small"
          label="Zip Code"
          value={details.zipCode}
          error={showErrors && missing.zipCode}
          helperText={err('zipCode', 'Zip Code')}
          onChange={e => onChange({ ...details, zipCode: e.target.value })}
        />
      </Grid>
      <Grid size={6}>
        <TextField
          fullWidth
          required
          size="small"
          label="Phone"
          value={details.phone}
          error={showErrors && missing.phone}
          helperText={err('phone', 'Phone')}
          onChange={e => onChange({ ...details, phone: e.target.value })}
        />
      </Grid>
      <Grid size={6}>
        <TextField
          fullWidth
          required
          size="small"
          label="Email"
          value={details.email}
          error={showErrors && missing.email}
          helperText={err('email', 'Email')}
          onChange={e => onChange({ ...details, email: e.target.value })}
        />
      </Grid>
    </Grid>
  )
}

function ContactFields({
  details,
  onChange,
  showErrors,
}: {
  details: ContactDetails
  onChange: (next: ContactDetails) => void
  showErrors: boolean
}) {
  const missing = contactMissing(details)
  const err = (key: keyof ContactDetails, label: string) => (showErrors && missing[key] ? `${label} is required` : '')

  return (
    <Grid container spacing="16px" width="100%">
      <Grid size={12}>
        <Typography>Fields marked with a * are required.</Typography>
      </Grid>
      <Grid size={6}>
        <TextField
          fullWidth
          required
          size="small"
          label="Name"
          value={details.name}
          error={showErrors && missing.name}
          helperText={err('name', 'Name')}
          onChange={e => onChange({ ...details, name: e.target.value })}
        />
      </Grid>
      <Grid size={6}>
        <TextField
          fullWidth
          required
          size="small"
          label="Title"
          value={details.title}
          error={showErrors && missing.title}
          helperText={err('title', 'Title')}
          onChange={e => onChange({ ...details, title: e.target.value })}
        />
      </Grid>
      <Grid size={6}>
        <TextField
          fullWidth
          required
          size="small"
          label="Phone"
          value={details.phone}
          error={showErrors && missing.phone}
          helperText={err('phone', 'Phone')}
          onChange={e => onChange({ ...details, phone: e.target.value })}
        />
      </Grid>
      <Grid size={6}>
        <TextField
          fullWidth
          required
          size="small"
          label="Email"
          value={details.email}
          error={showErrors && missing.email}
          helperText={err('email', 'Email')}
          onChange={e => onChange({ ...details, email: e.target.value })}
        />
      </Grid>
    </Grid>
  )
}
